import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "./i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import Header from "./components/store/Header";
import Footer from "./components/store/Footer";
import CookieBanner from "./components/store/CookieBanner";
import ProtectedRoute from "./components/store/ProtectedRoute";
import StickyCart from "./components/store/StickyCart";
import WhatsAppButton from "./components/store/WhatsAppButton";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import OrderTracking from "./pages/OrderTracking";
import Wishlist from "./pages/Wishlist";
import Account from "./pages/Account";
import { Login, Register } from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import About from "./pages/About";
import Contact from "./pages/Contact";
import { PrivacyPage, TermsPage, ReturnsPage, ShippingPage, FaqPage } from "./pages/StaticPages";
import AdminLayout from "./components/store/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminProductEdit from "./pages/admin/AdminProductEdit";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminHomepage from "./pages/admin/AdminHomepage";
import AdminShipping from "./pages/admin/AdminShipping";
import AdminSeo from "./pages/admin/AdminSeo";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminNavigation from "./pages/admin/AdminNavigation";
import AdminFooter from "./pages/admin/AdminFooter";
import AdminSellers from "./pages/admin/AdminSellers";
import AdminSellerDetail from "./pages/admin/AdminSellerDetail";
import Sell from "./pages/Sell";
import SellApply from "./pages/SellApply";
import SellerStatus from "./pages/SellerStatus";
import SellerTerms from "./pages/SellerTerms";
import MarketplacePolicy from "./pages/MarketplacePolicy";
import SellerStore from "./pages/SellerStore";
import Seller from "./pages/Seller";

import "./App.css";

const ScrollToTop = () => {
  const loc = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback synchronously
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/track" element={<OrderTracking />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<AdminOrderDetail />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/:id" element={<AdminProductEdit />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="homepage" element={<AdminHomepage />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="seo" element={<AdminSeo />} />
            <Route path="navigation" element={<AdminNavigation />} />
            <Route path="footer" element={<AdminFooter />} />
            <Route path="social" element={<AdminSocial />} />
            <Route path="sellers" element={<AdminSellers />} />
            <Route path="sellers/:id" element={<AdminSellerDetail />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="/sell" element={<Sell />} />
          <Route path="/sell/apply" element={<SellApply />} />
          <Route path="/seller/status" element={<SellerStatus />} />
          <Route path="/seller/*" element={<Seller />} />
          <Route path="/store/:slug" element={<SellerStore />} />
          <Route path="/legal/seller-terms" element={<SellerTerms />} />
          <Route path="/legal/marketplace-policy" element={<MarketplacePolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/returns" element={<ReturnsPage />} />
          <Route path="/legal/shipping" element={<ShippingPage />} />
          <Route path="/faq" element={<FaqPage />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <StickyCart />
      <WhatsAppButton />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <SettingsProvider>
                <AppRouter />
                <Toaster position="top-center" richColors />
              </SettingsProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
